import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Box, useToast } from "@chakra-ui/react";
import { ApiMessageStack } from "components";
import { ActionPromptContainer } from "components/action-prompt/ActionPromptContainer";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { AccountGrsPendingLeadsStore } from "store/UserAccounts/UserAccount/AccountGrsPendingLeads.store";
import { TGrsPendingLeadDetail } from "types/grs-pending-lead.type";
import { TLead } from "types/lead.type";
import { TSelectOption } from "types/select-input.type";
import { formatGrsPendingLeadName } from "utils/grs-pending-leads.utils";
import { useIsMountedRef } from "utils/react-hooks/useIsMountedRef.hook";
import {
  GRS_REJECT_FORM_INITIAL_VALUES,
  TGrsRejectFormValues,
} from "utils/validation-schemas/grs-pending-lead-reject.validation";
import { GrsPendingLeadAcceptedStep } from "./GrsPendingLeadAcceptedStep";
import { GrsPendingLeadRejectStep } from "./GrsPendingLeadRejectStep";
import { GrsPendingLeadReviewStep } from "./GrsPendingLeadReviewStep";
import { GrsPendingLeadTermsStep } from "./GrsPendingLeadTermsStep";

enum GrsPendingLeadPromptStep {
  REVIEW = "review",
  TERMS = "terms",
  ACCEPTED = "accepted",
  REJECT = "reject",
}

interface IGrsPendingLeadPromptProps {
  pendingLeadId: number;
  accountGrsPendingLeadsStore: AccountGrsPendingLeadsStore;
  closePrompt: () => void;
}

export const GrsPendingLeadPrompt: FC<IGrsPendingLeadPromptProps> = ({
  pendingLeadId,
  accountGrsPendingLeadsStore,
  closePrompt,
}) => {
  const toast = useToast();
  const isMountedRef = useIsMountedRef();
  const [detail, setDetail] = useState<TGrsPendingLeadDetail | null>(null);
  const [detailStatus, setDetailStatus] = useState(
    ApiRequestStatusEnum.PENDING
  );
  const [step, setStep] = useState(GrsPendingLeadPromptStep.REVIEW);
  const [acceptedLead, setAcceptedLead] = useState<TLead | null>(null);
  const [reasonOptions, setReasonOptions] = useState<TSelectOption<number>[]>(
    []
  );
  const [reasonsStatus, setReasonsStatus] = useState(ApiRequestStatusEnum.NONE);
  const [rejectValues, setRejectValues] = useState(
    GRS_REJECT_FORM_INITIAL_VALUES
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const stepRef = useRef<HTMLDivElement>(null);

  const {
    fetchPendingLeadDetail,
    fetchRejectionReasons,
  } = accountGrsPendingLeadsStore;

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const loadedDetail = await fetchPendingLeadDetail(pendingLeadId);
        if (!isMountedRef.current) return;
        setDetail(loadedDetail);
        setDetailStatus(ApiRequestStatusEnum.SUCCESS);
      } catch (error) {
        if (!isMountedRef.current) return;
        setDetailStatus(ApiRequestStatusEnum.ERROR);
      }
    };

    fetchDetail();
  }, [fetchPendingLeadDetail, pendingLeadId, isMountedRef]);

  const loadRejectionReasons = useCallback(async () => {
    setReasonsStatus(ApiRequestStatusEnum.PENDING);

    try {
      const reasons = await fetchRejectionReasons();
      if (!isMountedRef.current) return;
      setReasonOptions(reasons.map(({ id, label }) => ({ value: id, label })));
      setReasonsStatus(
        reasons.length
          ? ApiRequestStatusEnum.SUCCESS
          : ApiRequestStatusEnum.ERROR
      );
    } catch (error) {
      if (!isMountedRef.current) return;
      setReasonsStatus(ApiRequestStatusEnum.ERROR);
    }
  }, [fetchRejectionReasons, isMountedRef]);

  const isRejectStepWithoutReasons =
    step === GrsPendingLeadPromptStep.REJECT &&
    reasonsStatus === ApiRequestStatusEnum.NONE;

  useEffect(() => {
    if (isRejectStepWithoutReasons) loadRejectionReasons();
  }, [isRejectStepWithoutReasons, loadRejectionReasons]);

  useEffect(() => {
    stepRef.current?.focus();
  }, [step, detailStatus, detail]);

  const leadName = detail ? formatGrsPendingLeadName(detail) : "";

  const backFromReject = useCallback((values: TGrsRejectFormValues) => {
    setRejectValues(values);
    setStep(GrsPendingLeadPromptStep.REVIEW);
  }, []);

  const submit = useCallback(
    async (request: () => Promise<void>) => {
      if (isSubmittingRef.current) return false;
      isSubmittingRef.current = true;
      setIsSubmitting(true);

      try {
        await request();
        return true;
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
        return false;
      } finally {
        isSubmittingRef.current = false;
        if (isMountedRef.current) setIsSubmitting(false);
      }
    },
    [toast, isMountedRef]
  );

  const showSuccessToast = useCallback(
    (description: string) =>
      toast({ ...DEFAULT_SUCCESS_TOAST_OPTIONS, description }),
    [toast]
  );

  const handleAccept = useCallback(
    (termsId: number) =>
      submit(async () => {
        const lead = await accountGrsPendingLeadsStore.acceptPendingLead(
          pendingLeadId,
          termsId
        );

        if (!isMountedRef.current) {
          showSuccessToast("GRS lead accepted successfully");
          return;
        }
        setAcceptedLead(lead);
        setStep(GrsPendingLeadPromptStep.ACCEPTED);
      }),
    [
      submit,
      accountGrsPendingLeadsStore,
      pendingLeadId,
      isMountedRef,
      showSuccessToast,
    ]
  );

  const handleReject = useCallback(
    async ({ rejectionReasonId, notes }: TGrsRejectFormValues) => {
      if (rejectionReasonId === null) return false;

      return submit(async () => {
        await accountGrsPendingLeadsStore.rejectPendingLead(
          pendingLeadId,
          rejectionReasonId,
          notes
        );

        showSuccessToast("GRS lead rejected successfully");
        if (isMountedRef.current) closePrompt();
      });
    },
    [
      submit,
      accountGrsPendingLeadsStore,
      pendingLeadId,
      isMountedRef,
      showSuccessToast,
      closePrompt,
    ]
  );

  const headers: Record<GrsPendingLeadPromptStep, string> = {
    [GrsPendingLeadPromptStep.REVIEW]: `GRS pending lead - ${leadName}`,
    [GrsPendingLeadPromptStep.TERMS]: `Accept this GRS lead - ${leadName}`,
    [GrsPendingLeadPromptStep.ACCEPTED]: "Lead Accepted",
    [GrsPendingLeadPromptStep.REJECT]: `Reject this GRS lead - ${leadName}`,
  };

  return (
    <ActionPromptContainer
      header={headers[step]}
      loadingStatus={detailStatus}
      body={
        !!detail && (
          <Box
            ref={stepRef}
            tabIndex={-1}
            role={"group"}
            aria-label={headers[step]}
            _focus={{ outline: "none" }}
          >
            {step === GrsPendingLeadPromptStep.REVIEW && (
              <GrsPendingLeadReviewStep
                detail={detail}
                onReject={() => setStep(GrsPendingLeadPromptStep.REJECT)}
                onAccept={() => setStep(GrsPendingLeadPromptStep.TERMS)}
              />
            )}
            {step === GrsPendingLeadPromptStep.TERMS && (
              <GrsPendingLeadTermsStep
                detail={detail}
                accountGrsPendingLeadsStore={accountGrsPendingLeadsStore}
                isSubmitting={isSubmitting}
                onAccept={handleAccept}
                onCancel={closePrompt}
              />
            )}
            {step === GrsPendingLeadPromptStep.ACCEPTED && !!acceptedLead && (
              <GrsPendingLeadAcceptedStep
                acceptedLead={acceptedLead}
                closePrompt={closePrompt}
              />
            )}
            {step === GrsPendingLeadPromptStep.REJECT && (
              <GrsPendingLeadRejectStep
                detail={detail}
                reasonOptions={reasonOptions}
                reasonsStatus={reasonsStatus}
                onRetryReasons={loadRejectionReasons}
                defaultValues={rejectValues}
                isSubmitting={isSubmitting}
                onBack={backFromReject}
                onReject={handleReject}
              />
            )}
          </Box>
        )
      }
    />
  );
};
